import * as React from 'react';
import { AuthDescription } from '../AuthDescription';
import { AuthForm } from '../AuthForm';
import { AuthFormError } from '../AuthFormError';
import { AuthFormRow } from '../AuthFormRow';
import { AuthHeading } from '../AuthHeading';
import {
  AuthRedirectMessage,
  AuthRedirectMessageType
} from '../AuthRedirectMessage';
import { Button } from '../../Forms/Button';
import { FormRow } from '../../Forms/FormRow';
import { validateEmail } from '../../../validators/email';
import { debounce } from '../../../utils/debounce';
import { validatePasswordWithConfirm } from '../../../validators/password';
import './style.css';

export interface ResetPasswordProps {
  error: boolean;
  loading: boolean;
  onResetPassword: (email: string, password: string) => void;
}

export interface ResetPasswordState {
  confirmPassword: string;
  password: string;
  passwordError?: string;
  email: string;
  emailError?: string;
}

export class ResetPassword extends React.Component<
  ResetPasswordProps,
  ResetPasswordState
> {
  private debouncedValidateEmail: (email?: string) => void;

  constructor(props: ResetPasswordProps) {
    super(props);

    this.state = {
      confirmPassword: '',
      email: '',
      password: ''
    };

    // Debounce to prevent rapid revalidation as user types
    this.debouncedValidateEmail = debounce(this.validateEmail, 200);
  }

  render() {
    const { error, loading } = this.props;
    const {
      confirmPassword,
      password,
      passwordError,
      email,
      emailError
    } = this.state;

    return (
      <main className="reset-password">
        <AuthHeading>Reset your Edge of Play password</AuthHeading>
        <AuthDescription>
          Please enter the details below to set a new password for your Edge of
          Play account.
        </AuthDescription>
        <AuthForm onSubmit={this.tryResetPassword}>
          <AuthFormRow
            className="reset-password-form-row"
            inputClassName="reset-password-input"
            label="Your Email:"
            autoComplete="email"
            autoFocus={true}
            value={email}
            error={emailError}
            onChange={event =>
              this.updateEmail((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormRow
            className="reset-password-form-row"
            inputClassName="reset-password-input"
            label="New Password:"
            autoComplete="new-password"
            type="password"
            value={password}
            error={passwordError}
            onChange={event =>
              this.updatePassword((event.target as HTMLInputElement).value)
            }
          />
          <AuthFormRow
            className="reset-password-form-row"
            inputClassName="reset-password-input"
            label="Confirm Password:"
            type="password"
            value={confirmPassword}
            onChange={event =>
              this.updateConfirmPassword(
                (event.target as HTMLInputElement).value
              )
            }
          />
          <AuthFormError
            className="edge-of-play-form-row reset-password-error"
            error={
              error
                ? `Password reset failed, please check your credentials and try again`
                : undefined
            }
          />
          <FormRow>
            <Button
              className="reset-password-button"
              type="submit"
              disabled={loading}
            >
              Reset Password
            </Button>
          </FormRow>
          <FormRow>
            <AuthRedirectMessage
              type={AuthRedirectMessageType.RedirectToSignUp}
            />
          </FormRow>
        </AuthForm>
      </main>
    );
  }

  private shouldSubmitBeDisabled = (): boolean => {
    const { loading } = this.props;
    const {
      confirmPassword,
      email,
      emailError,
      password,
      passwordError
    } = this.state;

    return !!(
      loading ||
      !email ||
      !password ||
      !confirmPassword ||
      emailError ||
      passwordError
    );
  };

  private updateEmail = (email: string): void => {
    this.debouncedValidateEmail(email);
    this.setState({
      email
    });
  };

  private updatePassword = (password: string): void => {
    this.validatePassword(password, this.state.confirmPassword);
    this.setState({
      password
    });
  };

  private updateConfirmPassword = (confirmPassword: string): void => {
    this.validatePassword(this.state.password, confirmPassword);
    this.setState({
      confirmPassword
    });
  };

  private validateEmail = (email: string): void => {
    this.setState({
      emailError: validateEmail(email)
    });
  };

  private validatePassword = (
    password: string,
    confirmPassword: string
  ): void => {
    this.setState({
      passwordError: validatePasswordWithConfirm(password, confirmPassword)
    });
  };

  private tryResetPassword = (event: React.FormEvent<HTMLFormElement>) => {
    const { onResetPassword } = this.props;
    const { confirmPassword, password, email } = this.state;

    event.preventDefault();

    // Force validation before we check if we can submit
    this.validateEmail(email);
    this.validatePassword(password, confirmPassword);

    if (onResetPassword && !this.shouldSubmitBeDisabled()) {
      onResetPassword(email as string, password as string);
    }
  };
}



// WEBPACK FOOTER //
// ./src/app/components/Auth/ResetPassword/index.tsx